import { useState, useCallback, useMemo } from 'react';
import { formatNotifications, formatGhiChu, formatNumberPairs, formatEnergyDetails, formatCombinedPairs } from '../utils/formatters';
import dominantEnergyMeanings from '../data/dominantEnergyMeanings';
import { getMovingLineMeaning } from '../utils/hexagramUtils';

export const useAIContext = ({
  hexagramData,
  gender,
  ageRange,
  maritalStatus,
  job,
  concern,
  dominantEnergy,
  combinedPairs,
  notificationList,
  ghiChuList,
  warningNotice
}) => {
  const [context, setContext] = useState({});

  const updateContext = useCallback((newData) => {
    setContext(prev => ({
      ...prev,
      ...newData
    }));
  }, []);

  const contextString = useMemo(() => {
    const movingLineMeaning = getMovingLineMeaning(hexagramData?.main?.name, hexagramData?.movingLine);
    const dominantEnergyMeaning = dominantEnergy
      ? `Năng lượng chủ đạo **${dominantEnergy}** chi phối toàn bộ dãy số: 
         """${dominantEnergyMeanings[dominantEnergy]?.description || 'Đang hoàn thiện dữ liệu chuyên sâu'}"""`
      : 'Không xác định được năng lượng chủ đạo';

    let additionalNotices = [];
    if (notificationList?.length) {
      additionalNotices.push(`**Tổ hợp cảnh báo**:\n${formatNotifications(notificationList)}`);
    }
    if (ghiChuList?.length) {
      additionalNotices.push(`**Ghi chú đặc biệt**: ${formatGhiChu(ghiChuList)}`);
    }
    if (warningNotice) {
      additionalNotices.push(`**Cảnh báo số đuôi**: ${warningNotice.title} - ${warningNotice.subtitle}\n${warningNotice.points.map(p => `▸ ${p}`).join('\n')}`);
    }

    return `
      <user_data>
      - **SĐT**: ${hexagramData?.phoneNumber || 'N/A'}
      - **Nhân khẩu học**: ${gender || 'N/A'}, ${ageRange || 'N/A'}, ${maritalStatus || 'N/A'}
      - **Nghề nghiệp**: ${job || 'Chưa xác định'}
      - **Mối quan tâm chính**: **[${concern || 'N/A'}]**
      </user_data>

      <hexagram_data>
      - **Quẻ chính**: ${hexagramData?.main?.name || 'N/A'} 
        → *${hexagramData?.main?.meaning || 'N/A'}*
      - **Hào động ${hexagramData?.movingLine || 'N/A'}**: 
        → *${movingLineMeaning}*
      - **Quẻ biến**: ${hexagramData?.transformed?.name || 'N/A'} 
        → *${hexagramData?.transformed?.meaning || 'N/A'}*
      - **Các quẻ Dịch then chốt**:
      ${formatNumberPairs(hexagramData?.numberPairs)}
      </hexagram_data>

      <energy_data>
      - **Chuỗi tổ hợp năng lượng số**:
      ${formatEnergyDetails(combinedPairs)}
      - **Năng lượng chủ đạo**:
      ${dominantEnergyMeaning}
      - **Phân tích từng tổ hợp năng lượng số**:
      ${formatCombinedPairs(combinedPairs)}
      - **Cảnh báo đặc biệt**:
      ${additionalNotices.join('\n\n') || 'Không có cảnh báo đặc biệt'}
      </energy_data>`;
  }, [
    hexagramData,
    gender,
    ageRange,
    maritalStatus,
    job,
    concern,
    dominantEnergy,
    combinedPairs,
    notificationList,
    ghiChuList,
    warningNotice
  ]);

  return {
    context,
    contextString,
    updateContext
  };
};
